import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useFeatures, getEnabledFeatures } from './features';
import { searchPeople } from '../utils/papiSearch';
import { getPersonFullName, formatContactDisplayName } from '../utils/contactUtils';

export function usePapiSuggestions() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const [suggestions, setSuggestions] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const emailMap = useRef<Map<string, string>>(new Map());
  const nameMap = useRef<Map<string, string>>(new Map());
  const searchIdRef = useRef(0);

  const search = async (filteringText: string) => {
    if (!filteringText.trim()) {
      setSuggestions([]);
      setLoading(false);
      ++searchIdRef.current;
      return;
    }
    const thisSearchId = ++searchIdRef.current;
    setLoading(true);
    try {
      const people = await searchPeople(
        filteringText,
        queryClient,
        getEnabledFeatures(features, searchParams)
      );
      if (searchIdRef.current !== thisSearchId) return;
      if (people === null) {
        setSuggestions([]);
        return;
      }
      people.forEach((person) => {
        if (person.alias) {
          if (person.primaryEmail) emailMap.current.set(person.alias, person.primaryEmail);
          const fullName = getPersonFullName(person);
          if (fullName) nameMap.current.set(person.alias, fullName);
        }
      });
      setSuggestions(
        people
          .filter((p): p is typeof p & { alias: string } => !!p.alias)
          .map((person) => ({
            value: person.alias,
            label: formatContactDisplayName(getPersonFullName(person), person.alias),
          }))
      );
    } catch {
      if (searchIdRef.current === thisSearchId) setSuggestions([]);
    } finally {
      if (searchIdRef.current === thisSearchId) setLoading(false);
    }
  };

  return {
    suggestions,
    loading,
    search,
    getNameForAlias: (alias: string) => nameMap.current.get(alias),
    getEmailForAlias: (alias: string) => emailMap.current.get(alias),
  };
}
